<script setup lang="ts">
import { computed, watch } from 'vue';
import type { ValidationRule } from 'vuetify';
import CustomDatePicker from './custom-date-picker.vue';
import Tooltip from './custom-tooltip.vue';
import { mandatory } from '../../composables/validations';
import type { Household } from '../../types/household';

interface Props {
  disabled?: boolean;
  required?: boolean;
  readOnly?: boolean;
  stakeholder?: string;
  title?: string;
  withCompanyOption?: boolean;
  withDateOfBirth?: boolean;
  withManagingDirectorFields?: boolean;
}

const {
  disabled = false,
  required = false,
  readOnly = false,
  stakeholder = 'Auftraggeber',
  title = '',
  withCompanyOption = false,
  withDateOfBirth = false,
  withManagingDirectorFields = false,
} = defineProps<Props>();

const modelValue = defineModel<Household>({
  required: false,
  default: () => ({
    isCompany: false,
    companyName: '',
    salutation: '',
    dateOfBirth: '',
    firstName: '',
    lastName: '',
    registerNumber: '',
  }),
});

const isCompany = computed<boolean>({
  get: () => modelValue.value?.isCompany ?? false,
  set: (value: boolean) => {
    modelValue.value = { ...modelValue.value, isCompany: value };
  },
});

const salutation = computed<string>({
  get: () => modelValue.value.salutation ?? '',
  set: (value: string) => {
    modelValue.value = { ...modelValue.value, salutation: value };
  },
});

const firstName = computed<string>({
  get: () => modelValue.value.firstName ?? '',
  set: (value: string) => {
    modelValue.value = { ...modelValue.value, firstName: value };
  },
});

const lastName = computed<string>({
  get: () => modelValue.value.lastName ?? '',
  set: (value: string) => {
    modelValue.value = { ...modelValue.value, lastName: value };
  },
});

const dateOfBirth = computed<string>({
  get: () => modelValue.value.dateOfBirth ?? '',
  set: (value: string) => {
    modelValue.value = { ...modelValue.value, dateOfBirth: value };
  },
});

const companyName = computed<string>({
  get: () => modelValue.value.companyName ?? '',
  set: (value: string) => {
    modelValue.value = { ...modelValue.value, companyName: value };
  },
});

const registerNumber = computed<string>({
  get: () => modelValue.value.registerNumber ?? '',
  set: (value: string) => {
    modelValue.value = { ...modelValue.value, registerNumber: value };
  },
});

const rulesRequired = computed<ValidationRule[]>(() => (required ? mandatory : []));

const isSetToCompany = computed(() => withCompanyOption && isCompany.value);

const showManagingDirectorFields = computed(
  () => !isSetToCompany.value || withManagingDirectorFields,
);

const showAllCompanyFields = computed(
  () => isSetToCompany.value && showManagingDirectorFields.value,
);

const isDateOfBirthRequired = computed(() => {
  if (!withDateOfBirth) {
    return false;
  }
  if (isSetToCompany.value) {
    return !registerNumber.value;
  }
  return rulesRequired.value.length > 0;
});

const rulesDateOfBirth = computed(() => {
  if (!withDateOfBirth) {
    return [];
  }
  if (isSetToCompany.value) {
    return [
      (value?: string) => !!value
        || !!registerNumber.value
        || 'HR-Nummer oder Geburtsdatum benötigt!',
    ];
  }
  return rulesRequired.value;
});

const isRegisterNumberRequired = computed(() => {
  if (!withManagingDirectorFields || !isSetToCompany.value) {
    return false;
  }
  if (withDateOfBirth) {
    return !dateOfBirth.value;
  }
  return rulesRequired.value.length > 0;
});

const rulesRegisterNumber = computed(() => {
  if (!withManagingDirectorFields || !isSetToCompany.value) {
    return [];
  }
  if (withDateOfBirth) {
    return [
      (value?: string) => !!value
        || !!dateOfBirth.value
        || 'HR-Nummer oder Geburtsdatum benötigt!',
    ];
  }
  return rulesRequired.value;
});

watch(
  () => isSetToCompany.value,
  newValue => {
    if (newValue) {
      modelValue.value = {
        ...modelValue.value,
        salutation: '',
        dateOfBirth: '',
        firstName: '',
        lastName: '',
      };
    }
  },
);
</script>
<template>
  <v-row no-gutters>
    <v-col cols="12" align-self="start">
      <h3 v-if="title" class="pb-4">
        {{ title }}
      </h3>
      <v-switch
        v-if="withCompanyOption"
        v-model="isCompany"
        :label="`${stakeholder} ist Geschäftskunde`"
        hide-details
        :readonly="readOnly"
        :disabled="disabled"
        class="grow mb-2"
        density="compact"
      />
      <v-row no-gutters>
        <v-col v-if="isSetToCompany" cols="12">
          <v-text-field
            v-model="companyName"
            :rules="rulesRequired"
            :readonly="readOnly"
            :disabled="disabled"
            dense
            class="pb-2 pt-3"
          >
            <template #label>
              <span>Firmenname<sup v-if="rulesRequired" class="required">*</sup></span>
            </template>
          </v-text-field>
        </v-col>
      </v-row>
      <v-row no-gutters :class="{ 'main-infos': showAllCompanyFields }">
        <v-col v-if="showAllCompanyFields" cols="12">
          <h4 class="px-2 py-2">
            Angaben zum Geschäftsführer
          </h4>
        </v-col>
        <v-col cols="12" :class="{ 'px-5': showAllCompanyFields }">
          <template v-if="showManagingDirectorFields">
            <v-row no-gutters>
              <v-col cols="6">
                <h4 class="mb-2">
                  Anrede<sup v-if="rulesRequired" class="required">*</sup>
                </h4>
                <v-radio-group
                  v-model="salutation"
                  :rules="rulesRequired"
                  class="mb-4"
                  inline
                  :readonly="readOnly"
                  :disabled="disabled"
                  hide-details="auto"
                >
                  <v-radio label="Frau" value="Frau" />
                  <v-radio label="Herr" value="Herr" />
                </v-radio-group>
              </v-col>
              <v-col v-if="withDateOfBirth" cols="6">
                <custom-date-picker
                  label="Geburtsdatum"
                  class="pl-2"
                  should-be-adult
                  :custom-rules="rulesDateOfBirth"
                  :init-value="dateOfBirth"
                  :is-disabled="disabled"
                  :is-required="isDateOfBirthRequired"
                  @date-model="dateOfBirth = $event"
                />
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="6" class="pr-2">
                <v-text-field
                  v-model="firstName"
                  :rules="rulesRequired"
                  :readonly="readOnly"
                  :disabled="disabled"
                >
                  <template #label>
                    <span>Vorname<sup v-if="rulesRequired" class="required">*</sup></span>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="6" class="pl-2">
                <v-text-field
                  v-model="lastName"
                  :rules="rulesRequired"
                  :readonly="readOnly"
                  :disabled="disabled"
                >
                  <template #label>
                    <span>Nachname<sup v-if="rulesRequired" class="required">*</sup></span>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
          </template>
        </v-col>
      </v-row>
      <v-row v-if="showAllCompanyFields" no-gutters>
        <v-col cols="12" class="pt-4 pr-2">
          <v-text-field
            v-model="registerNumber"
            :rules="rulesRegisterNumber"
            :readonly="readOnly"
            :disabled="disabled"
          >
            <template #label>
              <span>
                Handelsregisternummer<sup v-if="isRegisterNumberRequired" class="required">*</sup>
              </span>
            </template>
            <template #append>
              <tooltip
                message="Nur erforderlich bei Firmierungen,
                  die unter das A-Register bzw. B-Register fallen (alle Kapitalgesellschaften)."
              />
            </template>
          </v-text-field>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>
<style scoped lang="scss">
  @use '../../assets/sass/variables';

  .main-infos {
    background-color: variables.lighten(variables.theme-color('primary'), 62%);
    padding: 10px;
    border-radius: 10px;
  }
</style>
